import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GameState, ArtistAction, GameEvent } from '@/types/career-sim';
import EnlargedCharacter from './EnlargedCharacter';
import HorizontalStatsBar from './HorizontalStatsBar';
import ActionCardDeck from './ActionCardDeck';
import ScreenFlash from './effects/ScreenFlash';
import FloatingText from './effects/FloatingText';
import WeekGoals from './WeekGoals';
import WeekResults from './WeekResults';
import EventCard from './EventCard';
import EventOutcome from './EventOutcome';
import UnlockNotification from './UnlockNotification';
import RecordingGame from '../minigames/RecordingGame';
import NetworkingGame from '../minigames/NetworkingGame';
import MarketingGame from '../minigames/MarketingGame';
import PerformanceGame from '../minigames/PerformanceGame';
import { checkUnlocks, getUnlockDetails } from '@/utils/unlockSystem';
import { shouldTriggerEvent, selectEvent, applyEventOutcome } from '@/utils/eventTriggerSystem';
import { useScreenShake } from '@/hooks/useScreenShake';

interface GameScreenProps {
  gameState: GameState;
  updateGameState: (updates: Partial<GameState>) => void;
}

const GameScreen: React.FC<GameScreenProps> = ({ gameState, updateGameState }) => {
  const [flashType, setFlashType] = useState<'success' | 'failure' | null>(null);
  const [floatingTexts, setFloatingTexts] = useState<Array<{ id: string; text: string; color: string }>>([]);
  const [showWeekGoals, setShowWeekGoals] = useState(false);
  const [showWeekResults, setShowWeekResults] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<GameEvent | null>(null);
  const [eventOutcome, setEventOutcome] = useState<{ choiceText: string; outcome: any; unlocks?: string[] } | null>(null);
  const [newUnlock, setNewUnlock] = useState<{ actionId: string; name: string; icon: string; description: string } | null>(null);
  const [activeMiniGame, setActiveMiniGame] = useState<'recording' | 'networking' | 'marketing' | 'performance' | null>(null);
  const [pendingAction, setPendingAction] = useState<ArtistAction | null>(null);
  
  const { shake } = useScreenShake();

  const handleActionStart = (action: ArtistAction) => {
    if (gameState.activeAction) return;

    // Check if player has enough energy
    if (gameState.needs.energy < action.energyCost) {
      setFlashType('failure');
      addFloatingText('Not enough energy!', 'text-red-400');
      shake();
      setTimeout(() => setFlashType(null), 300);
      return;
    }

    // Check if action has mini-game
    const miniGameMap: Record<string, typeof activeMiniGame> = {
      'create-music': 'recording',
      'perform-live': 'performance',
      'network': 'networking',
      'marketing-campaign': 'marketing'
    };

    const miniGame = miniGameMap[action.id];
    if (miniGame) {
      setPendingAction(action);
      setActiveMiniGame(miniGame);
      return;
    }

    // Execute action without mini-game
    executeAction(action, 1);
  };

  const executeAction = (action: ArtistAction, multiplier: number) => {
    // Apply action effects
    const updatedNeeds = { ...gameState.needs };
    updatedNeeds.energy = Math.max(0, updatedNeeds.energy - action.energyCost);

    // Apply outcomes with multiplier
    if (action.outcomes.creativity) {
      updatedNeeds.creativity = Math.min(100, updatedNeeds.creativity + action.outcomes.creativity * multiplier);
      if (action.outcomes.creativity > 0) {
        addFloatingText(`+${Math.round(action.outcomes.creativity * multiplier)} Creativity`, 'text-blue-400');
      }
    }
    if (action.outcomes.mentalHealth) {
      updatedNeeds.mentalHealth = Math.min(100, updatedNeeds.mentalHealth + action.outcomes.mentalHealth * multiplier);
      if (action.outcomes.mentalHealth > 0) {
        addFloatingText(`+${Math.round(action.outcomes.mentalHealth * multiplier)} Mental`, 'text-green-400');
      }
    }
    if (action.outcomes.energy) {
      updatedNeeds.energy = Math.min(100, updatedNeeds.energy + action.outcomes.energy * multiplier);
      if (action.outcomes.energy > 0) {
        addFloatingText(`+${Math.round(action.outcomes.energy * multiplier)} Energy`, 'text-yellow-400');
      }
    }

    const updates: Partial<GameState> = {
      activeAction: action,
      needs: updatedNeeds,
      actionCount: (gameState.actionCount || 0) + 1
    };

    if (action.outcomes.funds) {
      const fundsGained = Math.round(action.outcomes.funds * multiplier);
      updates.career = { ...gameState.career, funds: gameState.career.funds + fundsGained };
      addFloatingText(`+$${fundsGained}`, 'text-green-400');
    }
    if (action.outcomes.fanbase) {
      const fansGained = Math.round(action.outcomes.fanbase * multiplier);
      updates.career = { ...updates.career || gameState.career, fanbase: gameState.career.fanbase + fansGained };
      addFloatingText(`+${fansGained} Fans`, 'text-pink-400');
    }

    // Success flash
    setFlashType('success');
    setTimeout(() => setFlashType(null), 300);

    // Update game state
    updateGameState(updates);

    // Clear action after duration
    setTimeout(() => {
      updateGameState({ activeAction: null });
      
      // Check for unlocks
      const newUnlocks = checkUnlocks({ ...gameState, ...updates });
      if (newUnlocks.length > 0) {
        const unlockDetails = getUnlockDetails(newUnlocks[0]);
        if (unlockDetails) {
          setNewUnlock({
            actionId: newUnlocks[0],
            name: newUnlocks[0].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            icon: '🎯',
            description: 'New action available!'
          });
          updateGameState({ unlockedActions: [...gameState.unlockedActions, ...newUnlocks] });
        }
      }

      // Check for events
      if (shouldTriggerEvent({ ...gameState, ...updates }, action.id)) {
        const event = selectEvent({ ...gameState, ...updates }, action.id);
        if (event) {
          setCurrentEvent(event);
        }
      }

      // Check week progression (every 10 actions)
      if ((gameState.actionCount || 0) + 1 >= 10) {
        setShowWeekResults(true);
      }
    }, action.duration * 1000);
  };

  const handleMiniGameComplete = (result: any) => {
    setActiveMiniGame(null);
    if (pendingAction) {
      executeAction(pendingAction, result.multiplier);
      setPendingAction(null);
    }
  };

  const handleMiniGameExit = () => {
    setActiveMiniGame(null);
    setPendingAction(null);
  };

  const handleEventChoice = (choiceIndex: number) => {
    if (!currentEvent) return;
    
    const choice = currentEvent.choices[choiceIndex];
    setEventOutcome({
      choiceText: choice.text,
      outcome: choice.outcomes,
      unlocks: choice.outcomes.items?.map(i => i.id) || []
    });
    setCurrentEvent(null);
  };

  const handleEventOutcomeContinue = () => {
    if (eventOutcome) {
      const updates = applyEventOutcome(gameState, {
        ...eventOutcome.outcome,
        eventId: currentEvent?.id
      });
      updateGameState(updates);
    }
    setEventOutcome(null);
  };

  const handleWeekResultsContinue = () => {
    setShowWeekResults(false);
    updateGameState({
      currentWeek: gameState.currentWeek + 1,
      actionCount: 0
    });
    setShowWeekGoals(true);
  };

  const handleWeekGoalsContinue = () => {
    setShowWeekGoals(false);
  };

  const addFloatingText = (text: string, color: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setFloatingTexts(prev => [...prev, { id, text, color }]);
    setTimeout(() => {
      setFloatingTexts(prev => prev.filter(t => t.id !== id));
    }, 2000);
  };

  return (
    <div className="relative w-full h-full min-h-[800px] overflow-hidden rounded-xl border border-neon-purple/30 bg-gradient-to-b from-[#0a0a1a] to-[#1a0a2e]">
      {/* Background effects */}
      <div className="absolute inset-0 bg-cyber-grid bg-[size:50px_50px] opacity-5" />
      <div className="absolute inset-0 bg-gradient-radial from-neon-purple/10 via-transparent to-transparent opacity-30" />
      
      {/* Screen flash effects */}
      {flashType && <ScreenFlash type={flashType} />}

      {/* Floating text effects */}
      <div className="absolute inset-0 pointer-events-none z-50">
        {floatingTexts.map(ft => (
          <FloatingText key={ft.id} text={ft.text} color={ft.color} />
        ))}
      </div>

      {/* Top stats bar */}
      <div className="relative z-10">
        <HorizontalStatsBar gameState={gameState} />
      </div>

      {/* Main character area */}
      <div className="relative z-10 flex items-center justify-center py-12">
        <EnlargedCharacter
          personality={gameState.artistPersonality!}
          mood={gameState.currentMood}
          action={gameState.activeAction}
          needs={gameState.needs}
        />
      </div>

      {/* Action cards deck */}
      <div className="relative z-10 px-6 pb-8">
        <ActionCardDeck
          gameState={gameState}
          onActionStart={handleActionStart}
        />
      </div>

      {/* Mini-Games */}
      <AnimatePresence>
        {activeMiniGame === 'recording' && (
          <RecordingGame
            onComplete={handleMiniGameComplete}
            onExit={handleMiniGameExit}
          />
        )}
        {activeMiniGame === 'networking' && gameState.artistPersonality && (
          <NetworkingGame
            onComplete={handleMiniGameComplete}
            onExit={handleMiniGameExit}
            personality={gameState.artistPersonality.type}
          />
        )}
        {activeMiniGame === 'marketing' && gameState.artistPersonality && (
          <MarketingGame
            onComplete={handleMiniGameComplete}
            onExit={handleMiniGameExit}
            personality={gameState.artistPersonality.type}
          />
        )}
        {activeMiniGame === 'performance' && gameState.artistPersonality && (
          <PerformanceGame
            onComplete={handleMiniGameComplete}
            onExit={handleMiniGameExit}
            personality={gameState.artistPersonality.type}
          />
        )}
      </AnimatePresence>

      {/* Week Goals */}
      <AnimatePresence>
        {showWeekGoals && gameState.weeklyGoals && (
          <WeekGoals
            weeklyGoals={gameState.weeklyGoals}
            onContinue={handleWeekGoalsContinue}
          />
        )}
      </AnimatePresence>

      {/* Week Results */}
      <AnimatePresence>
        {showWeekResults && gameState.weeklyGoals && (
          <WeekResults
            weeklyGoals={gameState.weeklyGoals}
            goalsCompleted={true}
            onContinue={handleWeekResultsContinue}
          />
        )}
      </AnimatePresence>

      {/* Event Card */}
      <AnimatePresence>
        {currentEvent && (
          <EventCard
            event={currentEvent}
            onChoose={handleEventChoice}
            onDismiss={() => setCurrentEvent(null)}
          />
        )}
      </AnimatePresence>

      {/* Event Outcome */}
      <AnimatePresence>
        {eventOutcome && (
          <EventOutcome
            choiceText={eventOutcome.choiceText}
            outcome={eventOutcome.outcome}
            unlocks={eventOutcome.unlocks}
            onContinue={handleEventOutcomeContinue}
          />
        )}
      </AnimatePresence>

      {/* Unlock Notification */}
      <AnimatePresence>
        {newUnlock && (
          <UnlockNotification
            actionName={newUnlock.name}
            actionIcon={newUnlock.icon}
            description={newUnlock.description}
            onContinue={() => setNewUnlock(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameScreen;
